import React, { useCallback, useEffect, useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';

import type { IGigComplianceRow } from './types';
import type { ISetAdherenceReasonRequest } from '@/api/types';
import type { IContractRequirement } from '@/store/types';

import { FULFILLED_ACTION } from '@/config/variables';
import { useAppDispatch, useAppSelector, usePathParam, useWeek } from '@/hooks';
import { prepareGigComplianceToDisplay } from '@/helpers';
import {
  getAdherenceReason,
  getContractAdherence,
  getContractAttendance,
  getContractRequirementsAll,
  setAdherenceReason,
} from '@/store/slices/contractSlice';
import { selectIsMobileScreenSize } from '@/store/selectors/utilsSelectors';
import {
  selectContractAdherence,
  selectContractAdherenceReason,
  selectContractAttendance,
  selectIsPendingAction,
} from '@/store/selectors/contractSelectors';
import { PathParams } from '@/store/enums';
import { ROWS_TO_DISPLAY_ON_DESKTOP, ROWS_TO_DISPLAY_ON_MOBILE } from '@/constants/contracts';
import { BaseComplianceTable } from '@/components/common';

interface IProps {
  requirements: IContractRequirement | undefined;
}

const complianceTableSelector = createSelector(
  selectIsMobileScreenSize,
  selectContractAttendance,
  selectContractAdherence,
  selectContractAdherenceReason,
  selectIsPendingAction(getContractRequirementsAll.typePrefix),
  selectIsPendingAction(getContractAttendance.typePrefix),
  selectIsPendingAction(getContractAdherence.typePrefix),
  selectIsPendingAction(getAdherenceReason.typePrefix),
  (
    isMobileScreenSize,
    contractAttendance,
    contractAdherence,
    contractAdherenceReason,
    isLoadingContractRequirementsAll,
    isLoadingContractAttendance,
    isLoadingContractAdherence,
    isLoadingAdherenceReason,
  ) => ({
    isMobileScreenSize,
    contractAttendance,
    contractAdherence,
    contractAdherenceReason,
    isLoading:
      isLoadingContractRequirementsAll ||
      isLoadingContractAdherence ||
      isLoadingAdherenceReason ||
      isLoadingContractAttendance,
  }),
);

const ComplianceTable = ({ requirements }: IProps): React.ReactElement => {
  const dispatch = useAppDispatch();
  const { selectedWeekId } = useWeek();
  const [contractId] = usePathParam(PathParams.contractId);
  const { isMobileScreenSize, contractAttendance, contractAdherence, contractAdherenceReason, isLoading } =
    useAppSelector(complianceTableSelector);

  const weekIdArray: number[] = useMemo(() => {
    if (selectedWeekId === 0) return [];

    // prepare a list of weekId to display in rows
    const rowsLength = isMobileScreenSize ? ROWS_TO_DISPLAY_ON_MOBILE : ROWS_TO_DISPLAY_ON_DESKTOP;
    const weekOffset = rowsLength - 1; // to display current week at the end of list
    return [...Array(rowsLength).keys()].map(item => selectedWeekId + item - weekOffset);
  }, [selectedWeekId, isMobileScreenSize]);

  const rowsToDisplay: IGigComplianceRow[] = useMemo(() => {
    if (!selectedWeekId || !contractAdherence || !contractId || !contractAdherence[contractId]) return [];

    return prepareGigComplianceToDisplay(
      weekIdArray,
      contractAttendance.filter(x => x.contractId === contractId),
      contractAdherence[contractId],
      contractAdherenceReason,
    );
  }, [contractAdherence, contractAdherenceReason, contractAttendance, contractId, selectedWeekId, weekIdArray]);

  useEffect(() => {
    if (!contractId || selectedWeekId === 0) return;

    const getContractAttendanceCall = dispatch(getContractAttendance({ contractId, weekId: selectedWeekId })); // weekId is skipped on the backend
    const getContractAdherenceCall = dispatch(getContractAdherence({ contractId, weekId: selectedWeekId }));

    return () => {
      getContractAttendanceCall.abort();
      getContractAdherenceCall.abort();
    };
  }, [isMobileScreenSize, selectedWeekId, contractId, dispatch]);

  const getAdherenceReasons = useCallback(() => {
    if (!contractId) return;

    return dispatch(getAdherenceReason({ contractId, weekId: selectedWeekId }));
  }, [contractId, dispatch, selectedWeekId]);

  useEffect(() => {
    const getAdherenceReasonCall = getAdherenceReasons();

    return () => getAdherenceReasonCall?.abort();
  }, [contractId, getAdherenceReasons, selectedWeekId]);

  const handleSaveReason = (
    payload: Omit<ISetAdherenceReasonRequest, 'contractId' | 'selectedWeekId'>,
    closeModal: () => void,
  ) => {
    if (!contractId || !payload.weekId) return;
    dispatch(
      setAdherenceReason({
        contractId,
        weekId: payload.weekId,
        additionalReasonTxt: payload.additionalReasonTxt,
        reasonNote: payload.reasonNote,
        selectedWeekId,
      }),
    ).then(response => {
      if (response.meta.requestStatus === FULFILLED_ACTION) {
        getAdherenceReasons();
        closeModal();
      }
    });
  };

  return (
    <BaseComplianceTable
      rowsToDisplay={rowsToDisplay}
      requirements={requirements}
      handleSaveReason={handleSaveReason}
      isMobileScreenSize={isMobileScreenSize}
      isLoading={isLoading || rowsToDisplay.length === 0}
    />
  );
};

export { ComplianceTable };
